import { Box, xcss } from '@atlaskit/primitives';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import { submitFormOnEnter } from '@databases/form/Form';
import { defaultStoreUtils } from '@databases/redux';
import { selectFieldIdsByContentId } from '@databases/redux/database-view';
import { DatabaseFieldType, NumberFormats } from '@databases/types/database';
import { getNumberDisplayString } from '@databases/utils/database/field-types/number';
import React, {
  type CSSProperties,
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';
import { getExperimentValue } from '@canvas/client-feature-flags/experiments';

import {
  MAX_POPOVER_EDIT_WIDTH,
  TEXT_VALUE_MAX_TEXT_AREA_HEIGHT,
  TEXT_VALUE_MIN_TEXT_AREA_HEIGHT,
  useAdjustTextAreaDimensions
} from './popover-edit-common/index.ts';
import { type EditComponentProps } from './types.ts';

const textAreaContainerStyling = xcss({
  display: 'flex',
  flexDirection: 'row-reverse',
  boxSizing: 'border-box',
  position: 'relative',
  zIndex: 'dialog' // Ensure container sits above blue dots
});

const firstFieldStylings = xcss({
  flexDirection: 'row'
});

const textAreaMinHeightStyling = xcss({
  minHeight: 'size.400'
});

function getNumberEditorBorderStyles(
  isCompactDefaultValueInput: boolean,
  isFocused: boolean,
  isInlineTableEdit: boolean
): CSSProperties {
  const borderStyles: CSSProperties = {
    borderColor: token('color.background.accent.blue.subtle', '#579DFF'),
    borderStyle: 'solid',
    borderWidth: token('border.width.selected', '2px'),
    ...(isCompactDefaultValueInput ? {} : { borderRadius: 'radius.small' })
  };

  if (isCompactDefaultValueInput) {
    borderStyles.borderColor = token('color.border');
    borderStyles.borderRadius = getExperimentValue(
      'databasesContextualMenusM2',
      'enabled',
      { fireExperimentExposure: false }
    )
      ? token('radius.medium', '6px')
      : token('radius.medium');
    borderStyles.borderWidth = token('border.width', '1px');
    borderStyles.boxShadow = 'none';
    if (isFocused) {
      borderStyles.borderColor = token('color.border.focused');
    }
  } else if (isInlineTableEdit) {
    borderStyles.borderColor = 'transparent';
    borderStyles.boxShadow = 'none';
  }

  return borderStyles;
}

const numberFieldTextAlignment =
  'var(--database-number-field-text-align, right)' as React.CSSProperties['textAlign'];

const NumberValuePopoverEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.NUMBER>
> = ({
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  isInvalid,
  field,
  isDefaultValue,
  isRenderedInSidePanel,
  isRenderedInContextualFieldMenu,
  isBulkEdit,
  contentId,
  ariaLabel,
  initialKey,
  placeholder
}) => {
  const { locale } = useIntl();
  const hasRenderedRef = useRef(false);
  const maxWidthReachedRef = useRef<boolean>(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const formattedValue = useMemo(
    () => (value !== '' ? getNumberDisplayString(value, field, locale) : ''),
    [field, locale, value]
  );
  const [inputValue, setInputValue] = useState(initialKey ?? formattedValue);
  const [isFocused, setIsFocused] = useState(false);
  const isCompactDefaultValueInput =
    Boolean(isDefaultValue) && Boolean(isRenderedInContextualFieldMenu);
  const isInlineTableEdit =
    !isCompactDefaultValueInput && !isRenderedInSidePanel && !isBulkEdit;

  useEffect(() => {
    if (initialKey !== undefined) {
      onChange(initialKey, { type: DatabaseFieldType.NUMBER, value: initialKey });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const store = defaultStoreUtils.useStore();

  const adjustTextAreaDimensions = useAdjustTextAreaDimensions({
    textAreaRef,
    maxWidthReachedRef,
    maxPopoverEditWidth: isRenderedInSidePanel
      ? textAreaRef.current?.scrollWidth
      : MAX_POPOVER_EDIT_WIDTH,
    minTextAreaHeight: isCompactDefaultValueInput
      ? TEXT_VALUE_MIN_TEXT_AREA_HEIGHT
      : undefined,
    shouldExpandFromCell: !isCompactDefaultValueInput
  });

  useEffect(() => {
    adjustTextAreaDimensions();
  }, [inputValue, adjustTextAreaDimensions]);

  useEffect(() => {
    if (!hasRenderedRef.current) {
      hasRenderedRef.current = true;
      return;
    }

    if (!isFocused) {
      setInputValue(formattedValue);
    }
  }, [formattedValue, isFocused]);

  const fieldIds = selectFieldIdsByContentId(store.getState(), {
    contentId
  });

  const isFirstField = fieldIds.indexOf(field.id) === 0;

  function sanitizeNumberInputValue(inputValue: string): string {
    const isNegative = inputValue.startsWith('-');

    const sanitizedInput = inputValue.replace(/[^0-9.,]/g, '');

    return isNegative ? '-' + sanitizedInput : sanitizedInput;
  }

  const parseInput = useCallback(
    (localizedString: string) => {
      const formatter = new Intl.NumberFormat(locale);

      const parts = formatter.formatToParts(12345.6);
      let decimalSeparator = '.';
      let groupingSeparator = ',';
      for (const part of parts) {
        if (part.type === 'decimal') {
          decimalSeparator = part.value;
        } else if (part.type === 'group') {
          groupingSeparator = part.value;
        }
      }

      let withoutGroupingSeparators = localizedString.split(groupingSeparator).join('');

      // Take only the first occurrence of the decimal separator
      const decimalIndex = withoutGroupingSeparators.indexOf(decimalSeparator);
      if (decimalIndex !== -1) {
        withoutGroupingSeparators =
          withoutGroupingSeparators.slice(0, decimalIndex) +
          '.' +
          withoutGroupingSeparators
            .slice(decimalIndex + 1)
            .split(decimalSeparator)
            .join('');
      }

      return sanitizeNumberInputValue(withoutGroupingSeparators);
    },
    [locale]
  );

  const handleChange = useCallback(
    (e: React.FormEvent<HTMLTextAreaElement>): void => {
      let parsedValue = parseInput(e.currentTarget.value);
      setInputValue(e.currentTarget.value);

      if (
        parsedValue !== '' &&
        field.settings.numberFormat.format === NumberFormats.PERCENT
      ) {
        parsedValue = (Number(parsedValue) / 100).toString();
      }

      onChange(parsedValue, {
        type: DatabaseFieldType.NUMBER,
        value: parsedValue
      });
    },
    [parseInput, field.settings.numberFormat.format, onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => submitFormOnEnter(e),
    []
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(true);
      let cleanedValue = e.currentTarget.value;

      // Remove suffix if number format is percent
      if (field.settings.numberFormat.format === NumberFormats.PERCENT) {
        cleanedValue = cleanedValue.replace(/\s*%\s*/g, '');
      }

      // Remove prefix if it exists
      const prefix = field.settings.numberFormat.prefix;
      if (prefix && prefix !== '') {
        if (cleanedValue.startsWith(prefix)) {
          cleanedValue = cleanedValue.slice(prefix.length);
        }
      }

      // Update the input value if it changed
      if (cleanedValue !== e.currentTarget.value) {
        setInputValue(cleanedValue);
      }

      // Put cursor to the end of the field
      const cursorPosition = cleanedValue.length;
      e.currentTarget.setSelectionRange(cursorPosition, cursorPosition);

      adjustTextAreaDimensions();
      onFocus?.(e);
    },
    [
      adjustTextAreaDimensions,
      onFocus,
      field.settings.numberFormat.format,
      field.settings.numberFormat.prefix
    ]
  );

  const handlePaste = useCallback(() => {
    // Using setTimeout to ensure the pasted content is available in the textarea
    // Setting to 10 milliseconds to maximize the amount of time while NOT being
    // perceivable to humans
    setTimeout(() => {
      adjustTextAreaDimensions();
    }, 10);
  }, [adjustTextAreaDimensions]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    onBlur?.();
    setInputValue(formattedValue);
  }, [formattedValue, onBlur]);

  return (
    <Box
      testId={`number-value-edit-expand-${isFirstField ? 'right' : 'left'}`}
      xcss={[
        textAreaContainerStyling,
        isFirstField && firstFieldStylings,
        isRenderedInSidePanel &&
          !isRenderedInContextualFieldMenu &&
          textAreaMinHeightStyling
      ]}
    >
      <TextArea
        id={'NumberTextArea'}
        appearance={isInlineTableEdit ? 'none' : undefined}
        style={{
          ...getNumberEditorBorderStyles(
            isCompactDefaultValueInput,
            isFocused,
            isInlineTableEdit
          ),
          ...(isCompactDefaultValueInput && {
            background: token('color.background.input'),
            outline: 'none',
            paddingBlock: token('space.050', '4px'),
            paddingBlockEnd: token('space.025', '2px'),
            paddingBlockStart: token('space.075', '6px'),
            paddingInline: token('space.100', '8px'),
            minHeight: `${TEXT_VALUE_MIN_TEXT_AREA_HEIGHT}px`
          }),
          textAlign: isCompactDefaultValueInput ? 'left' : numberFieldTextAlignment,
          overflow: isCompactDefaultValueInput ? 'auto' : 'hidden',
          maxHeight: `${TEXT_VALUE_MAX_TEXT_AREA_HEIGHT}px`,
          position:
            isCompactDefaultValueInput || isRenderedInSidePanel || isBulkEdit
              ? 'relative'
              : 'absolute',
          boxSizing: 'border-box',
          // Override @atlaskit/textarea's font.body.large to match the cell's 14px font size.
          font: token('font.body', '14px')
        }}
        ref={textAreaRef}
        value={inputValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        isInvalid={isInvalid}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        minimumRows={isCompactDefaultValueInput ? 1 : undefined}
        rows={1}
        resize="none"
        wrap="off"
        data-testid="number-value-edit"
        aria-label={ariaLabel}
      />
    </Box>
  );
};

export default NumberValuePopoverEdit;
